import { useCallback, useEffect, useState } from "react";

import { getDraftSessionIds, DRAFTS_CHANGED_EVENT } from "@/lib/drafts";

/**
 * Reactively track the set of session IDs that currently have a non-empty
 * draft in localStorage.
 *
 * The hook reads the draft map on mount and then listens for the custom
 * {@link DRAFTS_CHANGED_EVENT} dispatched by `drafts.ts` whenever `setDraft`,
 * `clearDraft`, or `clearAllDrafts` is called.  It also listens to the native
 * `storage` event so changes from other tabs/windows stay in sync.
 *
 * @returns A `Set<string>` of session IDs with active drafts.
 */
export function useDraftSessionIds(): Set<string> {
  const [draftIds, setDraftIds] = useState<Set<string>>(() => getDraftSessionIds());

  const refresh = useCallback(() => {
    setDraftIds(getDraftSessionIds());
  }, []);

  useEffect(() => {
    // Re-read in case drafts changed between initial state and effect.
    refresh();

    window.addEventListener(DRAFTS_CHANGED_EVENT, refresh);
    window.addEventListener("storage", refresh);

    return () => {
      window.removeEventListener(DRAFTS_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  return draftIds;
}
